import { GTM_CONTAINER_ID } from '@/lib/analytics/gtmRouteGate';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';

/** Delay before tearing down a lazy-loaded GTM container after the last conversion push. */
export const GTM_CONVERSION_UNLOAD_DELAY_MS = 5000;

type GtmWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
  google_tag_manager?: Record<string, unknown>;
};

let lazyLoadedGtm = false;
let unloadTimer: ReturnType<typeof setTimeout> | undefined;

function hasGtmStartQueued(dataLayer: Array<Record<string, unknown>>): boolean {
  return dataLayer.some(
    (entry) => entry.event === 'gtm.js' || entry['gtm.start'] != null
  );
}

function injectGtmScript(): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  const anchor = document.getElementsByTagName('script')[0];
  if (anchor?.parentNode) {
    anchor.parentNode.insertBefore(script, anchor);
    return;
  }
  document.head.appendChild(script);
}

function gtmScriptSelector(): string {
  return `script[src*="googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}"]`;
}

/** True when the GTM container script is already on the page. */
export function isGtmContainerLoaded(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const gtmWindow = window as GtmWindow;
  if (gtmWindow.google_tag_manager?.[GTM_CONTAINER_ID]) {
    return true;
  }

  return Boolean(document.querySelector(gtmScriptSelector()));
}

/**
 * Inject the GTM container when absent. Intentionally skips the pre-auth session
 * cookie guard from `buildGtmBootstrapScript` — used only for conversion pushes
 * that fire after sign-in (GRO-3977 funnel gate would otherwise block GTM).
 */
export function loadGtmContainer(): void {
  // TELEMETRY-GATE: GTM container must not load in suppressed builds.
  if (
    typeof window === 'undefined' ||
    !isSaasTelemetryEnabled ||
    isGtmContainerLoaded()
  ) {
    return;
  }

  const gtmWindow = window as GtmWindow;
  gtmWindow.dataLayer = gtmWindow.dataLayer ?? [];
  if (!hasGtmStartQueued(gtmWindow.dataLayer)) {
    gtmWindow.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  }

  injectGtmScript();
  lazyLoadedGtm = true;
}

/**
 * Remove a lazy-loaded GTM container. No-op for containers injected by `_document`
 * on the pre-auth funnel (those never set `lazyLoadedGtm`).
 */
export function unloadGtmContainer(): void {
  if (typeof window === 'undefined' || !lazyLoadedGtm) {
    return;
  }

  document.querySelectorAll(gtmScriptSelector()).forEach((node) => {
    node.remove();
  });

  const gtmWindow = window as GtmWindow;
  if (gtmWindow.google_tag_manager?.[GTM_CONTAINER_ID]) {
    delete gtmWindow.google_tag_manager[GTM_CONTAINER_ID];
  }

  lazyLoadedGtm = false;
}

function scheduleGtmUnloadAfterConversion(): void {
  if (!lazyLoadedGtm) {
    return;
  }

  if (unloadTimer) {
    clearTimeout(unloadTimer);
  }

  unloadTimer = setTimeout(() => {
    unloadTimer = undefined;
    unloadGtmContainer();
  }, GTM_CONVERSION_UNLOAD_DELAY_MS);
}

/**
 * Marketing conversion-only helper (`signup_conversion`, `signup_successful`).
 * Lazy-loads GTM when absent (bypassing the GRO-3977 session gate on purpose),
 * pushes the event, then tears down lazy-loaded containers after
 * `GTM_CONVERSION_UNLOAD_DELAY_MS` so tags can fire without persisting on
 * clinical pages. Do not reuse for general analytics — funnel `_document` gate
 * remains the default GTM entry point.
 *
 * No-op on SSR and on suppressed builds.
 */
export function pushGtmDataLayerEvent(event: Record<string, unknown>): void {
  // TELEMETRY-GATE: conversion dataLayer pushes must not reach GTM when
  // telemetry is suppressed.
  if (typeof window === 'undefined' || !isSaasTelemetryEnabled) {
    return;
  }

  const gtmWindow = window as GtmWindow;
  gtmWindow.dataLayer = gtmWindow.dataLayer ?? [];
  gtmWindow.dataLayer.push(event);
  loadGtmContainer();
  scheduleGtmUnloadAfterConversion();
}

/** Test-only reset for module state. */
export function resetGtmConversionUnloadStateForTests(): void {
  if (unloadTimer) {
    clearTimeout(unloadTimer);
    unloadTimer = undefined;
  }
  lazyLoadedGtm = false;
}
